import React, { useState, useEffect, useRef } from "react";
import {
  Card, CardHeader, CardBody, CardTitle, Row, Col, Button,
  Alert, Spinner, Input, Label, FormGroup, Badge,
} from "reactstrap";
import axios from "axios";
import { mdToHtml, stripMarkdownFence, PRINT_CSS } from "../../utils/markdown";

const CURRICULUM = process.env.REACT_APP_API_URL_CURRICULUM_SKILLS;
const FTTI = process.env.REACT_APP_API_URL_FUTURE_TECHNOLOGY_TRENDS_IDENTIFIER;

// FTTI is served behind the authenticated user-management gateway.
const ftAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("accessTokenSkillab")}` });

const errText = (err, fallback = "Something went wrong") =>
  err?.response?.data?.detail || err?.message || fallback;

// The generate endpoint now runs the LLM job asynchronously: the first POST
// (and every one after it, as long as the same analyses are selected) either
// comes back "running" — meaning the job is still in progress server-side —
// or "completed" with the recommendations. We poll by resending the same
// request until it reports "completed".
const REPORT_POLL_INTERVAL_MS = 5000;


const GenerateReport = ({ industryOnly = false }) => {
  // ---- source analyses ----
  const [fttiTitles, setFttiTitles] = useState([]);
  const [fttiLoading, setFttiLoading] = useState(false);
  const [fttiTitle, setFttiTitle] = useState("");

  const [shortRuns, setShortRuns] = useState([]);
  const [shortLoading, setShortLoading] = useState(false);
  const [shortTitle, setShortTitle] = useState("");

  const [longRuns, setLongRuns] = useState([]);
  const [longLoading, setLongLoading] = useState(false);
  const [longTitle, setLongTitle] = useState("");

  // ---- options ----
  const [focus, setFocus] = useState("");
  const [forceRefresh, setForceRefresh] = useState(false);

  // ---- result ----
  const [report, setReport] = useState(null);       // markdown string
  const [reportMeta, setReportMeta] = useState(null); // {cached, used_analyses, not_found, created_at}
  const [generating, setGenerating] = useState(false);
  const [generatingStatus, setGeneratingStatus] = useState(""); // "running" wait message from the API
  const [error, setError] = useState(null);

  // Tracks whether the component is still mounted, so a poll response that
  // arrives after unmount doesn't try to update state; also holds the
  // pending poll timeout so it can be cancelled (unmount, Cancel, Clear).
  const mountedRef = useRef(true);
  const pollTimeoutRef = useRef(null);

  // ---- load the selectable analyses ----
  useEffect(() => {
    loadFttiTitles();
    if (!industryOnly) {
      loadShortRuns();
      loadLongRuns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cancel any in-flight poll on unmount.
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, []);

  const loadFttiTitles = async () => {
    setFttiLoading(true);
    try {
      const res = await axios.get(`${FTTI}/analyses/titles`, { headers: ftAuth() });
      setFttiTitles(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError((prev) => prev || `Could not load Future Technology Trends analyses: ${errText(e)}`);
    } finally {
      setFttiLoading(false);
    }
  };

  const loadShortRuns = async () => {
    setShortLoading(true);
    try {
      const res = await axios.get(`${CURRICULUM}/policy/runs`);
      const raw = res.data;
      setShortRuns(Array.isArray(raw) ? raw : (raw?.runs || raw?.data || []));
    } catch (e) {
      setError((prev) => prev || `Could not load Short Term analyses: ${errText(e)}`);
    } finally {
      setShortLoading(false);
    }
  };

  const loadLongRuns = async () => {
    setLongLoading(true);
    try {
      const res = await axios.get(`${CURRICULUM}/skill-gap/longterm/gap-by-title/runs`);
      const raw = res.data;
      setLongRuns(Array.isArray(raw) ? raw : (raw?.runs || raw?.data || []));
    } catch (e) {
      setError((prev) => prev || `Could not load Long Term analyses: ${errText(e)}`);
    } finally {
      setLongLoading(false);
    }
  };

  const anySelected = !!fttiTitle || !!shortTitle || !!longTitle;

  const generate = async () => {
    setError(null);
    if (!anySelected) {
      setError("Select at least one analysis to include in the report.");
      return;
    }
    // Field mapping:
    //   FTTI trends   -> tsouk_title   (fetched live from the Tsouk API)
    //   Short Term    -> policy_title
    //   Long Term     -> longterm_title
    // (shortterm_title / skill-hotness is intentionally not used here.)
    const basePayload = {
      tsouk_title: fttiTitle || null,
      policy_title: shortTitle || null,
      longterm_title: longTitle || null,
      focus: focus.trim() || null,
    };

    setGenerating(true);
    setGeneratingStatus("");

    // The endpoint has no job id: polling means resending the same request.
    // Only the very first request should carry force_refresh — every poll
    // after that must send force_refresh: false, otherwise each poll tick
    // would kick off a brand new computation instead of checking on it.
    const poll = async (isFirstRequest) => {
      const payload = { ...basePayload, force_refresh: isFirstRequest ? forceRefresh : false };
      try {
        const res = await axios.post(`${CURRICULUM}/recommendations/generate`, payload, { headers: ftAuth() });
        if (!mountedRef.current) return;
        const data = res.data || {};

        if (data.status === "running") {
          setGeneratingStatus(data.message || "Your analysis is already running. Check back shortly.");
          pollTimeoutRef.current = setTimeout(() => poll(false), REPORT_POLL_INTERVAL_MS);
          return;
        }

        if (data.status && data.status !== "completed") {
          setReport(null);
          setReportMeta(null);
          setError(data.message || `Unexpected report status: ${data.status}`);
          setGenerating(false);
          setGeneratingStatus("");
          return;
        }

        // status === "completed" (or an older response with no status field at all)
        setReport(data.recommendations || "");
        setReportMeta({
          cached: !!data.cached,
          used_analyses: data.used_analyses,
          not_found: data.not_found || [],
          created_at: data.created_at || null,
        });
        if (!data.recommendations) {
          setError("The report came back empty. Try again or adjust the selected analyses.");
        }
        setGenerating(false);
        setGeneratingStatus("");
      } catch (e) {
        if (!mountedRef.current) return;
        setReport(null);
        setReportMeta(null);
        setError(`Could not generate the report: ${errText(e)}`);
        setGenerating(false);
        setGeneratingStatus("");
      }
    };

    poll(true);
  };

  const cancelGenerating = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setGenerating(false);
    setGeneratingStatus("");
  };

  const clearAll = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setGenerating(false);
    setGeneratingStatus("");
    setFttiTitle("");
    setShortTitle("");
    setLongTitle("");
    setFocus("");
    setForceRefresh(false);
    setReport(null);
    setReportMeta(null);
    setError(null);
  };

  const printReport = () => {
    if (!report) return;
    const html =
      `<!doctype html><html><head><meta charset="utf-8">` +
      `<title>Curriculum Recommendations</title><style>${PRINT_CSS}</style></head>` +
      `<body>${mdToHtml(stripMarkdownFence(report))}</body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        // ignore
      }
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 300);
  };

  const reportHtml = report ? mdToHtml(stripMarkdownFence(report)) : "";

  return (
    <div className="content">
      <Card>
        <CardHeader>
          <CardTitle tag="h4" className="mb-1">Generate Report</CardTitle>
          <span style={{ color: "#666" }}>
            {industryOnly
              ? "Build an LLM report from a Future Technology Trends analysis you have already run."
              : "Build an LLM curriculum-recommendations report from analyses you have already run. Pick any combination of a Future Technology Trends analysis, a Short Term Analysis and a Long Term Analysis."}
          </span>
        </CardHeader>
        <CardBody>
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            {/* Future Technology Trends analysis -> tsouk_title */}
            <Col md={industryOnly ? "6" : "4"}>
              <FormGroup>
                <Label>
                  <i className="fas fa-lightbulb" style={{ marginRight: 6, color: "#51bcda" }}></i>
                  Future Technology Trends analysis
                </Label>
                <Input
                  type="select"
                  value={fttiTitle}
                  onChange={(e) => setFttiTitle(e.target.value)}
                  disabled={fttiLoading}
                >
                  <option value="">{fttiLoading ? "Loading…" : "— none —"}</option>
                  {fttiTitles.filter((t) => t.title).map((t) => (
                    <option key={t.title} value={t.title}>
                      {t.title}{t.sector ? ` (${t.sector})` : ""}
                    </option>
                  ))}
                </Input>
                {!fttiLoading && fttiTitles.length === 0 && (
                  <small className="text-muted">No analyses found.</small>
                )}
              </FormGroup>
            </Col>

            {!industryOnly && (
              <>
                {/* Short Term Analysis -> policy_title */}
                <Col md="4">
                  <FormGroup>
                    <Label>
                      <i className="fas fa-bolt" style={{ marginRight: 6, color: "#ef8157" }}></i>
                      Short Term Analysis
                    </Label>
                    <Input
                      type="select"
                      value={shortTitle}
                      onChange={(e) => setShortTitle(e.target.value)}
                      disabled={shortLoading}
                    >
                      <option value="">{shortLoading ? "Loading…" : "— none —"}</option>
                      {shortRuns.filter((r) => r.title).map((r, i) => (
                        <option key={r.title || r.run_id || i} value={r.title}>
                          {r.title}{r.filters?.country ? ` · ${r.filters.country}` : ""}
                        </option>
                      ))}
                    </Input>
                    {!shortLoading && shortRuns.length === 0 && (
                      <small className="text-muted">No analyses found.</small>
                    )}
                  </FormGroup>
                </Col>

                {/* Long Term Analysis -> longterm_title */}
                <Col md="4">
                  <FormGroup>
                    <Label>
                      <i className="fas fa-chart-line" style={{ marginRight: 6, color: "#6bd098" }}></i>
                      Long Term Analysis
                    </Label>
                    <Input
                      type="select"
                      value={longTitle}
                      onChange={(e) => setLongTitle(e.target.value)}
                      disabled={longLoading}
                    >
                      <option value="">{longLoading ? "Loading…" : "— none —"}</option>
                      {longRuns.filter((r) => r.title).map((r, i) => (
                        <option key={r.title || r.run_id || i} value={r.title}>
                          {r.title}{r.filters?.country ? ` · ${r.filters.country}` : ""}
                        </option>
                      ))}
                    </Input>
                    {!longLoading && longRuns.length === 0 && (
                      <small className="text-muted">No analyses found.</small>
                    )}
                  </FormGroup>
                </Col>
              </>
            )}
          </Row>

          <Row>
            <Col md="8">
              <FormGroup>
                <Label>Focus <span style={{ color: "#999", fontWeight: 400 }}>(optional)</span></Label>
                <Input
                  bsSize="sm"
                  placeholder="e.g. focus on Greece, emphasise data-science skills…"
                  value={focus}
                  onChange={(e) => setFocus(e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md="4" className="d-flex align-items-center">
              <FormGroup check className="mt-2">
                <Label check>
                  <Input
                    type="checkbox"
                    checked={forceRefresh}
                    onChange={(e) => setForceRefresh(e.target.checked)}
                  />{" "}
                  Regenerate (ignore cached report)
                </Label>
              </FormGroup>
            </Col>
          </Row>

          {/* Selection summary */}
          {anySelected && (
            <div style={{ marginBottom: 12 }}>
              <small className="text-muted">Included: </small>
              {fttiTitle && <Badge color="info" style={{ marginRight: 4 }}>Trends: {fttiTitle}</Badge>}
              {shortTitle && <Badge color="warning" style={{ marginRight: 4 }}>Short term: {shortTitle}</Badge>}
              {longTitle && <Badge color="success" style={{ marginRight: 4 }}>Long term: {longTitle}</Badge>}
            </div>
          )}

          <Button color="primary" onClick={generate} disabled={!anySelected || generating}>
            {generating ? <><Spinner size="sm" /> Generating…</> : "Generate report"}
          </Button>{" "}
          {generating ? (
            <Button color="secondary" outline onClick={cancelGenerating}>
              Cancel
            </Button>
          ) : (
            (anySelected || report) && (
              <Button color="secondary" outline onClick={clearAll}>
                Clear
              </Button>
            )
          )}
          {!anySelected && !generating && (
            <small className="text-muted ml-2">Select at least one analysis.</small>
          )}

          {generating && (
            <Alert color="info" className="mt-3 mb-0">
              <Spinner size="sm" style={{ marginRight: 8 }} />
              {generatingStatus || "Generating your report…"}
            </Alert>
          )}
        </CardBody>
      </Card>

      {/* ---- Report preview ---- */}
      {report != null && (
        <Card>
          <CardHeader>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <CardTitle tag="h5" className="mb-1">Recommendations</CardTitle>
                {reportMeta && (
                  <span style={{ color: "#666" }}>
                    {reportMeta.cached ? (
                      <Badge color="secondary">cached{reportMeta.created_at ? ` · ${new Date(reportMeta.created_at).toLocaleDateString()}` : ""}</Badge>
                    ) : (
                      <Badge color="success">freshly generated</Badge>
                    )}
                  </span>
                )}
              </div>
              <Button color="info" outline size="sm" onClick={printReport} disabled={!report}>
                <i className="fas fa-print" style={{ marginRight: 6 }}></i>Print report
              </Button>
            </div>
            {reportMeta && reportMeta.not_found && reportMeta.not_found.length > 0 && (
              <Alert color="warning" className="mt-2 mb-0">
                No saved analysis found for: {reportMeta.not_found.join(", ")}. The report used the rest.
              </Alert>
            )}
          </CardHeader>
          <CardBody>
            {report ? (
              <div className="report-markdown" dangerouslySetInnerHTML={{ __html: reportHtml }} />
            ) : (
              <em style={{ color: "#999" }}>No recommendations were returned.</em>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default GenerateReport;
